import React, { useEffect, useState } from "react";
import gql from "graphql-tag";
import { useQuery } from "@apollo/client";
import clone from "clone";
import { Store } from "./store";
import Helmet from "react-helmet";
import ReactGA from "react-ga";
import ReactGA4 from "react-ga4";
import TagManager from "react-gtm-module";
import Color from "color";
import Parse from "html-react-parser";

require("./settings.json");
const env = require("./env.json");
const isProduction = env.BUILD_ENV === "production";

export default (props) => {
  const { data, refetch } = useQuery(GET_CONFIG, { fetchPolicy: "cache-and-network", variables: { themeRef: env.THEME_REF } });
  const [fontFaces, setFontFaces] = useState();
  const [customCss, setCustomCss] = useState();

  const configFromApi = data?.config && clone(data.config);

  // Every time we reload, CSS gets injected
  useEffect(() => {
    if (!configFromApi) return;
    const clonedConfig = clone(configFromApi);
    const theme = clonedConfig.eshop.theme.selected;
    if (!theme) return;

    const root = document.documentElement;

    let headCss = "";

    if (theme.settings.collectionColumns) {
      headCss += `
        @media only screen and (max-width: 840px) {
          :root {
            --collection-columns: ${theme.settings.collectionColumns.mobile};
          }
        }
        @media only screen and (min-width: 840px) {
          :root {
            --collection-columns: ${theme.settings.collectionColumns.tablet};
          }
        }
        @media only screen and (min-width: 1080px) {
          :root {
            --collection-columns: ${theme.settings.collectionColumns.desktop};
          }
        }
      `;
    }

    if (theme.settings.gutter) {
      headCss += `
        @media only screen and (max-width: 840px) {
          :root {
            --gutter: ${theme.settings.gutter.mobile}px;
          }
        }
        @media only screen and (min-width: 840px) {
          :root {
            --gutter: ${theme.settings.gutter.tablet}px;
          }
        }
        @media only screen and (min-width: 1080px) {
          :root {
            --gutter: ${theme.settings.gutter.desktop}px;
          }
        }
      `;
    }

    if (theme.colors && theme.colors.presets) {
      headCss += `
        :root {
          ${theme.colors.presets.map(p => `--${p.id}: ${p.value};`).join("\n")}
        }
      `;
    }

    if (theme.buttons) {
      const getButtonProps = (selector, button) => `
        ${selector} {
          ${button.backgroundColor ? `background-color: var(--${button.backgroundColor.default?.id});` : ""}
          ${button.border ? `
            border-radius: ${button.border.radius || 0}px;
            border-width: ${button.border.width || 0}px;
            border-style: ${button.border.style || "none"};
            ${button.border.color ? `border-color: var(--${button.border.color.default?.id});` : ""};
          ` : ""}
          ${button.padding ? `
            padding: ${button.padding.vertical || 0}px ${button.padding.horizontal || 0}px;
          ` : ""}
        }
        ${selector}:hover {
          ${button.backgroundColor?.hover ? `background-color: var(--${button.backgroundColor.hover.id});` : ""}
          ${button.border?.color?.hover ? `border-color: var(--${button.border.color.hover.id});` : ""}
        }
        ${selector}:active {
          ${button.backgroundColor?.active ? `background-color: var(--${button.backgroundColor.active.id});` : ""}
          ${button.border.color ? `border-color: var(--${button.border.color.active?.id});` : ""}
        }
      `;

      if (theme.buttons.primary) {
        headCss += getButtonProps("button.primary", theme.buttons.primary);
      }
      if (theme.buttons.buy) {
        headCss += getButtonProps("#item .itemButton", theme.buttons.buy);
      }
    }

    if (theme.icons) {
      headCss += `
        @font-face {
          font-family: "cg-icons";
          src: url("${theme.icons.fontUri}?v=2")
            format("${theme.icons.format}");
          font-weight: normal;
          font-style: normal;
          font-display: block;
        }
      `;
    } else {
      headCss += `
        @font-face {
          font-family: "cg-icons";
          src: url("https://static.common-ground.io/common/fonts/eshop/cg-font-eshop-v2.woff2?v=2")
            format("woff2");
          font-weight: normal;
          font-style: normal;
          font-display: block;
        }
      `;
    }

    setCustomCss(headCss + (theme.css || ""));

    if (theme.settings.maxWidth)
      root.style.setProperty("--max-width", `${theme.settings.maxWidth}px`);
    if (theme.settings.borderRadius)
      root.style.setProperty("--border-radius", `${theme.settings.borderRadius}px`);

    let styleElementContent = "";
    if (theme.styles && theme.styles.length) {
      styleElementContent = theme.fontFaces;
      for (const style of theme.styles) {
        styleElementContent += style.css;
      }
      setFontFaces(styleElementContent);
    }

    clonedConfig.getProperty = function (moduleId, property) {
      const module = this.modules.find(m => m.id === moduleId);
      if (module && module.data) return module.data[property];
      return null;
    };

    clonedConfig.getColorValueForId = function (id) {
      const color = this.eshop.theme.selected.colors.presets.find(p => p.id === id);
      return color ? color.value : null;
    };

    clonedConfig.getModalStyles = function () {
      const settings = clonedConfig.eshop?.theme?.selected?.settings;
      const backgroundVar = settings.backgroundColor?.value ? `rgba(${Color(settings.backgroundColor?.value).rgb().array().join(",")}, 0.8)` : "inherit";

      return {
        overlay: {
          border: "none",
          backgroundColor: backgroundVar
        },
        content: {
          border: "none",
          backgroundColor: `var(--${this.eshop.theme.selected.settings.backgroundColor?.id})`,
          top: "50%",
          left: "50%",
          right: "auto",
          bottom: "auto",
          minWidth: "30%",
          marginRight: "-50%",
          transform: "translate(-50%, -50%)"
        }
      };
    };

    const googleTagManager = clonedConfig.getProperty("googleAnalytics", "gtm");
    if (googleTagManager) TagManager.initialize({ gtmId: googleTagManager });

    const configUA = clonedConfig.google?.analytics?.view?.name || clonedConfig.getProperty("googleAnalytics", "id");
    const GATracker = clonedConfig.google?.analytics?.property?.tracker;

    const isUA = !!(configUA && configUA.includes("UA-"));
    const UATracker = isUA && clonedConfig.google?.analytics?.view?.name?.match(/^UA-[^ ]*/)[0];
    const testMode = !isProduction;

    if (UATracker) ReactGA.initialize(UATracker, { testMode });

    const trackers = [{ trackingId: "G-SY3JXR6N3C", testMode }];
    if (GATracker) trackers.push({ trackingId: GATracker, testMode });

    if (!isProduction) console.log("Trackers", trackers, UATracker, "Config is using old UA", isUA, "Test mode:", testMode);
    ReactGA4.initialize(trackers);

    ReactGA4.set({ appName: clonedConfig.getProperty("information", "shopName") });
    ReactGA4.set({ appId: String(clonedConfig.id) });
    ReactGA4.set({ hostname: clonedConfig.uri });
    ReactGA4.set({ user_properties: { origin: clonedConfig.uri } });

    Store.update(s => { s.config = clonedConfig; s.reloadConfig = refetch; });
  }, [configFromApi]);

  const headTags = configFromApi?.eshop?.head?.tags;
  return <>
    <Helmet>
      <title>{configFromApi?.shopName}</title>
      <style rel="stylesheet">{fontFaces}</style>
      <style rel="stylesheet">{customCss}</style>
      {headTags ? Parse(headTags.join(""), {}) : null}
    </Helmet>
    {props.children}
  </>;
};

const COLOR_FIELDS = `
  id
  name
  value
`;

const BUTTON_FIELDS = `
  padding {
    vertical
    horizontal
  }
  backgroundColor {
    default {
      ${COLOR_FIELDS}
    }
    active {
      ${COLOR_FIELDS}
    }
    hover {
      ${COLOR_FIELDS}
    }
  }
  border {
    color {
      default {
        ${COLOR_FIELDS}
      }
      active {
        ${COLOR_FIELDS}
      }
      hover {
        ${COLOR_FIELDS}
      }
    }
    width
    radius
    style
  }
`;

const GET_CONFIG = gql(`
query($domain: String, $themeRef: ID) {
  config(domain: $domain, themeRef: $themeRef) {
    id
    uri
    domain
    currencySymbol
    currencySymbolPosition
    shopName
    modules {
      data
      id
    }
    eshop {
      menus
      theme {
        selected {
          _id
          css
          fontFaces
          colors {
            presets {
              ${COLOR_FIELDS}
            }
          }
          pages {
            item {
              suggestions {
                display
              }
              layout {
                id
                title
              }
            }
          }
          buttons {
            primary {
              ${BUTTON_FIELDS}
            }
            buy {
              ${BUTTON_FIELDS}
            }
          }
          player {
            active
            backgroundColor {
              ${COLOR_FIELDS}
            }
            waveformColor {
              ${COLOR_FIELDS}
            }
            progressColor {
              ${COLOR_FIELDS}
            }
            cursorColor {
              ${COLOR_FIELDS}
            }
            cursorWidth
            barRadius
            barWidth
            barGap
          }
          settings {
            maxWidth
            mobileBreakpoint
            borderRadius
            gutter {
              mobile
              tablet
              desktop
            }
            collectionColumns {
              desktop
              mobile
              tablet
            }
            logo {
              width
            }
            layout {
              id
              title
            }
            superHeader {
              display
              backgroundColor {
                ${COLOR_FIELDS}
              }
            }
            wantlist {
              enabled
            }
            header {
              backgroundColor {
                ${COLOR_FIELDS}
              }
              logoWidth {
                mobile
                tablet
                desktop
              }
              sticky
              layout {
                id
                title
              }
            }
            navbar {
              layout {
                id
                title
              }
            }
            footer {
              layout {
                id
                title
              }
              backgroundColor {
                id
                value
                name
              }
            }
            backgroundColor {
              id
              value
              name
            }
          }
          styles {
            _id
            title
            css
          }
          icons {
            fontUri
            format
          }
        }
      }
      checkout {
        version
      }
      head {
        tags
      }
      preferences {
        languages {
          whitelist
        }
        homepage {
          type
          handle
        }
      }
    }
    google {
      analytics {
        view {
          id
          name
        }
        property {
          id
          name
          tracker
        }
      }
    }
    articles {
      handle
      title
      type
    }
    socials {
      id
      value
      title
    }
    taxes {
      rules {
        editPricesBeforeTaxes
        showPricesBeforeTaxes
      }
    }
  }
}
`);
